import {QuestionModel} from '@/models/content/question';
import { taxonomySerializer } from './taxonomy';
import { sessionService } from '@/providers/services/auth/session';
import { DEFAULT_IMAGES_PATH, DEFAULT_CATALOG_STRING } from '@/utils/constants';
import { findAnyCompetencyTitle, gutCourseNames, truncateString } from '@/utils/utils';
import moment from 'moment';

export class QuestionSerializer {
  private static INSTANCE = new QuestionSerializer();

  static get instance() {
    return this.INSTANCE;
  }

  public serializeQuestion(question: any): QuestionModel {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = question.thumbnail ?
      contentCdnUrl + question.thumbnail :
        `${window.location.origin}` + DEFAULT_IMAGES_PATH.unit;
    const result: QuestionModel = {
      id: question.id,
      title: question.title,
      description: question.description,
      subformat: question.contentSubFormat,
      format: question.format || 'question',
      efficacy: question.efficacy,
      relevance: question.relevance,
      engagement: question.engagement,
      standards: taxonomySerializer.normalizeTaxonomyObject(question.taxonomy),
      creator: taxonomySerializer.normalizeCreator(question.creator),
      owner: taxonomySerializer.normalizeCreator(question.creator),
      isVisibleOnProfile: false,
      thumbnailUrl,
    };
    return result;
  }

    /**
     * Normalized data of resource by id
     * @return {Object}
     */
    public normalizeQuestionContent(response: any) {
      const taxonomy = taxonomySerializer.normalizeTaxonomyObject(response.taxonomy);
      response.taxonomy = taxonomy[0];
      return response;
    }


  public normalizeSearchQuestions(payload: any) {
    const resultSet =  {
      searchResults: [],
      hitCount: payload.totalHitCount,
    };
    if (Array.isArray(payload.searchResults)) {
      const results = payload.searchResults.map((result: any) => {
        return this.normalizeQuestion(result);
      });
      resultSet.searchResults =  results;
    }
    return resultSet;
  }

  /**
   * Normalizes a question
   * @param {*} result
   * @returns {Question}
   */
  public normalizeQuestion(questionData: any) {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = questionData.thumbnail ?
      contentCdnUrl + questionData.thumbnail :
        `${window.location.origin}` + DEFAULT_IMAGES_PATH.unit;
    const taxonomyInfo =
      (questionData.taxonomySet &&
        questionData.taxonomySet.curriculum &&
        questionData.taxonomySet.curriculum.curriculumInfo) ||
      [];
    const format = DEFAULT_CATALOG_STRING.question[questionData.contentSubFormat].name;
    const standards = taxonomySerializer.normalizeTaxonomyArray(taxonomyInfo, false);
    const description = questionData.description
    ? questionData.description
    : questionData.text;
    return {
      id: questionData.gooruOid,
      title: questionData.title,
      subformat: questionData.contentSubFormat,
      description: description || findAnyCompetencyTitle(standards) || questionData.title,
      type: questionData.contentFormat ? questionData.contentFormat : null,
      format,
      publisher: null, // TODO missing publisher at API response,
      thumbnailUrl,
      creator: questionData.creator
        ? taxonomySerializer.normalizeCreator(questionData.creator)
        : null,
      owner: questionData.user
        ? taxonomySerializer.normalizeOwner(questionData.user)
        : null,
      standards,
      taxonomySet: questionData.taxonomySet,
      taxonomySubject: questionData.taxonomySet
        ? questionData.taxonomySet.subject
        : null,
      taxonomyCourse: questionData.taxonomySet
        ? questionData.taxonomySet.course
        : null,
      taxonomyDomain: questionData.taxonomySet
        ? questionData.taxonomySet.domain
        : null,
      efficacy: questionData.efficacy ? questionData.efficacy : null,
      relevance: questionData.relevance ? questionData.relevance : null,
      engagement: questionData.engagement ? questionData.engagement : null,
      contentSubFormat: questionData.contentSubFormat,
      publisherQualityIndicator: questionData.publisherQualityIndicator,
    };
  }

  public questionPulloutSerializer(collection: any, selectedQuestion: any, metadataLevels: any) {

    const defaultVectorValue = 0.5;
    let resultSet = {};
    if (collection) {
      const advertisement_level = metadataLevels.advertisement_level;
      const audience = metadataLevels.audience;
      const grade = metadataLevels.grade;
      const license = metadataLevels.license;
      const educational_use = metadataLevels.educational_use;
      const gradeLevelText: any = [];
      const collectionGrade = collection.metadata
        ? collection.metadata.grade
        : null;
      if (collectionGrade) {
        collectionGrade.forEach((gradeLevel: any) => {
          gradeLevelText.push(grade[gradeLevel]);
        });
      }

      resultSet = {
        descriptive: {
          title: collection.title,
          description: truncateString(collection.description),
        },
        creation: {
          'Published By': 'Gooru org',
          'Published Status': 'Published',
          'License': collection.license
            ? license[collection.license]
            : 'Public Domain',
          'created by': collection.owner && collection.owner.username || null,
          'created on': collection.publish_date
            ? moment(collection.publish_date).format('MMMM DD, YYYY')
            : null,
          'modified on': collection.modeified_date
            ? collection.modeified_date
            : null,
          'modified by': collection.modified_by,
        },

        educational: {
          'language': collection.info
            ? collection.info.language === 'eng'
              ? 'English'
              : collection.info.language
            : 'English',
          'educational use': collection.metadata
            ? educational_use[collection.metadata.educational_use]
            : null,
          'accessbility': collection.accessibility,
          'grade': collection.metadata
            ? collection.metadata.grade
              ? gradeLevelText.join(', ')
              : null
            : null,
          'age-range': collection.age ? collection.age : null,
          'Editorial Range': selectedQuestion.publisherQualityIndicator,
          'keywords': collection.info
            ? collection.info.keywords
              ? collection.info.keywords[0]
              : null
            : null,
          'audience': collection.metadata
            ? collection.metadata.audience
              ? audience[collection.metadata.audience]
              : null
            : null,
        },

        media: {
          'format': collection.content_subformat
            ? collection.content_subformat.replace(/_/g, ' ')
            : null,
          'media Features': collection.media ? collection.media : null,
          'access hazard': collection.accesshazard
            ? collection.accesshazard
            : 'None',
          'advertisement level': collection.metadata
            ? collection.metadata.advertisement_level
              ? advertisement_level[collection.metadata.advertisement_level]
              : 'Low'
            : 'Low',
          'framebreaker': collection.display_guide
            ? collection.display_guide.is_frame_breaker === 1
              ? 'Yes'
              : 'No'
            : 'No',
          'Is Broken': collection.publish_date
            ? collection.publish_date.is_broken
            : 'No',
          'address': collection.address ? collection.address : 'None',
        },

        instructional: {
          'Depth of Knowledge': collection.depthofknowledge.map((item: any) => item.label).toString(),
          '21st Century Skills': collection.skills,
        },

        framework: {
          subject: selectedQuestion.taxonomySubject,
          course: selectedQuestion.taxonomyCourse
            ? gutCourseNames(selectedQuestion.taxonomyCourse).join(', ')
            : null,
          domain: selectedQuestion.taxonomyDomain
            ? selectedQuestion.taxonomyDomain.join(', ')
            : null,
          standard: selectedQuestion.standards ? selectedQuestion.standards : [],
        },

        vector: {
          relevance: selectedQuestion.relevance || defaultVectorValue,
          engagement: selectedQuestion.engagement || defaultVectorValue,
          efficacy: selectedQuestion.efficacy || defaultVectorValue,
        },
      };
    }
    return resultSet;
  }

}

export const questionSerializer = QuestionSerializer.instance;
